/* eslint-disable react-hooks/exhaustive-deps */
// pages/ZonePlanPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  FiMapPin, FiEdit2, FiCheck, FiX, FiRefreshCw,
  FiChevronDown, FiChevronUp, FiInfo,
  FiSearch, FiZap,
} from "react-icons/fi";
import axios from "axios";
import { BsCurrencyRupee } from "react-icons/bs";

// ── API helpers ──────────────────────────────────────────────────────────
const BASE = axios.create({ baseURL: `${process.env.REACT_APP_SOCKET_URL}/api` });
const hdrs = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

async function fetchAllZonePrices() {
  try { const r = await BASE.get("/zone-plans", hdrs()); return r.data; }
  catch (e) { return { success: false, error: e.response?.data?.message || e.message }; }
}
async function fetchZonePrices(zoneId) {
  try { const r = await BASE.get(`/zone-plans/${zoneId}`, hdrs()); return r.data; }
  catch (e) { return { success: false, error: e.response?.data?.message || e.message }; }
}
async function bulkSetPrices(zoneId, prices) {
  try { const r = await BASE.put(`/zone-plans/${zoneId}/bulk`, { prices }, hdrs()); return r.data; }
  catch (e) { return { success: false, error: e.response?.data?.message || e.message }; }
}
async function deleteOverride(zoneId, planName) {
  try { const r = await BASE.delete(`/zone-plans/${zoneId}/${planName}`, hdrs()); return r.data; }
  catch (e) { return { success: false, error: e.response?.data?.message || e.message }; }
}
async function fetchAllZones(orgId) {
  try { const r = await BASE.get(`/zones/${orgId}/zones`, hdrs()); return r.data; }
  catch (e) { return { success: false, error: e.response?.data?.message || e.message }; }
}

// ── Plan meta ────────────────────────────────────────────────────────────
// const PLANS = ["TRAIL", "SILVER", "GOLD", "PLATINUM"];
const PLAN_META = {
  TRAIL:    { color: "#64748b", bg: "#f1f5f9", label: "Trail",    duration: "6 months" },
  SILVER:   { color: "#475569", bg: "#e2e8f0", label: "Silver",   duration: "1 month"  },
  GOLD:     { color: "#b45309", bg: "#fef3c7", label: "Gold",     duration: "6 months" },
  PLATINUM: { color: "#6d28d9", bg: "#ede9fe", label: "Platinum", duration: "1 year"   },
};

// ── Toast ────────────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
      {toasts.map((t) => (
        <div key={t.id} style={{
          background: t.type === "error" ? "#fef2f2" : "#ecfdf5",
          border: `1px solid ${t.type === "error" ? "#fecaca" : "#a7f3d0"}`,
          color:  t.type === "error" ? "#b91c1c" : "#065f46",
          padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
          display: "flex", alignItems: "center", gap: 8, minWidth: 220,
          animation: "slideUp 0.2s ease",
        }}>
          {t.type === "error" ? <FiX size={14} /> : <FiCheck size={14} />}
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ── Plan badge ────────────────────────────────────────────────────────────
function PlanBadge({ plan }) {
  const m = PLAN_META[plan];
  return (
    <span style={{
      background: m.bg, color: m.color,
      fontSize: 10, fontWeight: 800, letterSpacing: "0.06em",
      padding: "3px 8px", borderRadius: 6, textTransform: "uppercase",
    }}>{m.label}</span>
  );
}

// ── Zone pricing row (inline edit) ───────────────────────────────────────
function ZonePricingRow({ zone, onSaved, showToast }) {
  const [expanded, setExpanded]   = useState(false);
  const [pricing, setPricing]     = useState(null);   // resolved per-plan data
  const [editing, setEditing]     = useState(false);
  const [form, setForm]           = useState({});     // { SILVER: "500", ... }
  const [saving, setSaving]       = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadDetail = useCallback(async () => {
    if (pricing) return; // already loaded
    setLoadingDetail(true);
    const res = await fetchZonePrices(zone.zoneId);
    if (res.success) setPricing(res.pricing);
    else showToast(res.error || "Failed to load", "error");
    setLoadingDetail(false);
  }, [zone.zoneId, pricing]);

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) await loadDetail();
  };

  const startEdit = () => {
    const initial = {};
    (pricing || []).forEach((p) => { initial[p.planName] = String(p.amount); });
    setForm(initial);
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setForm({}); };

  const saveEdit = async () => {
    setSaving(true);
    const prices = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "" && !isNaN(Number(v))) prices[k] = Number(v);
    });
    const res = await bulkSetPrices(zone.zoneId, prices);
    if (res.success) {
      showToast(`Prices updated for ${zone.name || zone.zoneName}`);
      setEditing(false);
      // Refresh detail
      const r2 = await fetchZonePrices(zone.zoneId);
      if (r2.success) setPricing(r2.pricing);
      onSaved();
    } else {
      showToast(res.error || "Save failed", "error");
    }
    setSaving(false);
  };

  const handleRevert = async (planName) => {
    const res = await deleteOverride(zone.zoneId, planName);
    if (res.success) {
      showToast(`${planName} reverted to global default`);
      const r2 = await fetchZonePrices(zone.zoneId);
      if (r2.success) setPricing(r2.pricing);
      onSaved();
    } else {
      showToast(res.error || "Revert failed", "error");
    }
  };

  const hasOverrides = pricing?.some((p) => p.isOverridden);

  return (
    <div style={{
      border: "1.5px solid var(--sp-slate-200)",
      borderRadius: 14,
      overflow: "hidden",
      background: "#fff",
      boxShadow: expanded ? "var(--sp-shadow)" : "var(--sp-shadow-sm)",
      transition: "box-shadow 0.18s",
    }}>
      {/* Zone header row */}
      <button
        onClick={handleExpand}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", padding: "16px 20px",
          background: "none", border: "none", cursor: "pointer",
          gap: 12, textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: "var(--sp-emerald-50)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FiMapPin size={16} color="var(--sp-emerald-700)" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--sp-slate-900)", display: "flex", alignItems: "center", gap: 8 }}>
              {zone.name || zone.zoneName}
              {hasOverrides && (
                <span style={{
                  fontSize: 10, fontWeight: 700, background: "var(--sp-emerald-100)",
                  color: "var(--sp-emerald-700)", padding: "2px 7px", borderRadius: 5, letterSpacing: "0.05em",
                }}>
                  CUSTOM PRICING
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--sp-slate-500)", marginTop: 2 }}>
              {zone.zoneId} {zone.type ? `· ${zone.type}` : ""}
            </div>
          </div>
        </div>

        {/* Quick price preview (from overview) */}
        {!expanded && zone.prices && (
          <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
            {["SILVER", "GOLD", "PLATINUM"].map((p) =>
              zone.prices[p] !== undefined ? (
                <div key={p} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: "var(--sp-slate-400)", fontWeight: 600, textTransform: "uppercase" }}>{p.slice(0,2)}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--sp-slate-800)" }}>₹{zone.prices[p].toLocaleString("en-IN")}</div>
                </div>
              ) : null
            )}
          </div>
        )}

        <div style={{ flexShrink: 0, color: "var(--sp-slate-400)" }}>
          {expanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: "1px solid var(--sp-slate-100)", padding: "16px 20px 20px" }}>
          {loadingDetail ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--sp-slate-400)", fontSize: 13, padding: "12px 0" }}>
              <div className="spinner" style={{ width: 16, height: 16, borderColor: "var(--sp-emerald-500)", borderTopColor: "transparent" }} />
              Loading prices…
            </div>
          ) : pricing ? (
            <>
              {/* Action buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
                {editing ? (
                  <>
                    <button className="btn-outline-green" style={{ fontSize: 12, padding: "6px 14px" }} onClick={cancelEdit} disabled={saving}>
                      Cancel
                    </button>
                    <button className="btn btn-success" style={{ fontSize: 12, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6 }} onClick={saveEdit} disabled={saving}>
                      {saving ? <div className="spinner" style={{ width: 12, height: 12, borderColor: "#fff", borderTopColor: "transparent" }} /> : <FiCheck size={13} />}
                      Save Prices
                    </button>
                  </>
                ) : (
                  <button className="btn btn-success" style={{ fontSize: 12, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6 }} onClick={startEdit}>
                    <FiEdit2 size={13} /> Edit Prices
                  </button>
                )}
              </div>

              {/* Plan pricing grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12 }}>
                {pricing.map((p) => {
                  const meta = PLAN_META[p.planName];
                  return (
                    <div key={p.planName} style={{
                      border: `1.5px solid ${p.isOverridden ? meta.bg : "var(--sp-slate-200)"}`,
                      borderRadius: 12, padding: "14px 16px",
                      background: p.isOverridden ? `${meta.bg}55` : "var(--sp-slate-50)",
                      position: "relative",
                    }}>
                      {/* Override indicator */}
                      {p.isOverridden && (
                        <div style={{
                          position: "absolute", top: 10, right: 10,
                          width: 7, height: 7, borderRadius: "50%",
                          background: meta.color, opacity: 0.8,
                        }} title="Zone override active" />
                      )}

                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                        <PlanBadge plan={p.planName} />
                      </div>

                      {editing ? (
                        <div>
                          <label style={{ fontSize: 11, color: "var(--sp-slate-500)", fontWeight: 600, display: "block", marginBottom: 4 }}>
                            Zone Price (₹)
                          </label>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--sp-slate-400)", fontSize: 13 }}>₹</span>
                            <input
                              type="number"
                              min="0"
                              value={form[p.planName] ?? ""}
                              onChange={(e) => setForm((f) => ({ ...f, [p.planName]: e.target.value }))}
                              style={{
                                width: "100%", boxSizing: "border-box",
                                border: "1.5px solid var(--sp-slate-200)", borderRadius: 8,
                                padding: "7px 10px 7px 24px", fontSize: 14, fontWeight: 700,
                                color: "var(--sp-slate-900)", outline: "none",
                              }}
                              onFocus={(e) => e.target.style.borderColor = "var(--sp-emerald-500)"}
                              onBlur={(e) => e.target.style.borderColor = "var(--sp-slate-200)"}
                            />
                          </div>
                          {p.globalDefault !== p.amount && (
                            <div style={{ fontSize: 11, color: "var(--sp-slate-400)", marginTop: 5 }}>
                              Global default: ₹{p.globalDefault?.toLocaleString("en-IN")}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: meta.color, letterSpacing: "-0.5px" }}>
                            ₹{p.amount?.toLocaleString("en-IN")}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--sp-slate-400)", marginTop: 3 }}>{meta.duration}</div>

                          {/* Override vs default info */}
                          {p.isOverridden ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                              <span style={{ fontSize: 10, color: meta.color, fontWeight: 700 }}>
                                Zone override
                              </span>
                              <button
                                onClick={() => handleRevert(p.planName)}
                                title="Revert to global default"
                                style={{
                                  background: "none", border: "none", cursor: "pointer",
                                  fontSize: 10, color: "var(--sp-slate-400)", display: "flex",
                                  alignItems: "center", gap: 3, padding: "2px 0",
                                }}
                              >
                                <FiRefreshCw size={11} /> Reset
                              </button>
                            </div>
                          ) : (
                            <div style={{ fontSize: 10, color: "var(--sp-slate-400)", marginTop: 10, fontWeight: 600 }}>
                              Using global default
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function ZonePlanPage() {
  const [zones, setZones]         = useState([]); // all zones from /api/zones
  const [overviewMap, setOverviewMap] = useState({}); // { zoneId: { prices } }
  const [search, setSearch]       = useState("");
  const [loading, setLoading]     = useState(true);
  const [toasts, setToasts]       = useState([]);

  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  };

  // Load zones list + existing overrides overview
  const loadAll = useCallback(async () => {
    setLoading(true);
    const adminData = localStorage.getItem("admin");
    console.log("adminData", adminData);
    const userData = adminData ? JSON.parse(adminData) : null;
    const orgId = userData?.orgId || "";
    const [zonesRes, overviewRes] = await Promise.all([fetchAllZones(orgId), fetchAllZonePrices()]);

    if (zonesRes.success) {
      setZones(zonesRes.zones || zonesRes.data || []);
    }
    if (overviewRes.success) {
      const map = {};
      (overviewRes.zones || []).forEach((z) => { map[z.zoneId] = z; });
      setOverviewMap(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, []);

  const filteredZones = zones.filter((z) =>
    !search ||
    z.name?.toLowerCase().includes(search.toLowerCase()) ||
    z.zoneId?.toLowerCase().includes(search.toLowerCase())
  );

  const overriddenCount = Object.keys(overviewMap).length;

  return (
    <div style={{ padding: "0 0 40px" }}>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Page header */}
      <div className="sf-page-header">
        <div>
          <h2 style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 32, height: 32, borderRadius: 9, background: "var(--sp-emerald-50)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <BsCurrencyRupee  size={16} color="var(--sp-emerald-700)" />
            </span>
            Zone Plan Pricing
          </h2>
          <p>Set custom plan prices per zone. Falls back to global default if no override is set.</p>
        </div>
        <button
          className="btn btn-success"
          style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13 }}
          onClick={loadAll}
          disabled={loading}
        >
          <FiRefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="stat-grid" style={{ marginBottom: 20, gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "var(--sp-emerald-50)" }}>
            <FiMapPin size={18} color="var(--sp-emerald-700)" />
          </div>
          <div className="stat-value">{zones.length}</div>
          <div className="stat-label">Total Zones</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "var(--sp-violet-100)" }}>
            <FiZap size={18} color="var(--sp-violet-700)" />
          </div>
          <div className="stat-value">{overriddenCount}</div>
          <div className="stat-label">Custom Pricing Set</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "var(--sp-slate-100)" }}>
            <FiInfo size={18} color="var(--sp-slate-500)" />
          </div>
          <div className="stat-value">{zones.length - overriddenCount}</div>
          <div className="stat-label">Using Global Default</div>
        </div>
      </div>

      {/* Info banner */}
      <div style={{
        background: "var(--sp-emerald-50)", border: "1px solid var(--sp-emerald-100)",
        borderRadius: 12, padding: "12px 16px", marginBottom: 20,
        display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--sp-emerald-700)",
      }}>
        <FiInfo size={15} style={{ flexShrink: 0 }} />
        <span>
          Zone overrides take priority over global plan prices. If no override is set for a zone, drivers will be charged the global default price.
          Use the <strong>Reset</strong> button on any plan card to revert to the global default.
        </span>
      </div>

      {/* Search */}
      <div className="card section" style={{ padding: "14px 16px", marginBottom: 16 }}>
        <div className="search-box">
          <FiSearch size={14} className="search-icon" />
          <input
            placeholder="Search zones by name or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="search-clear" onClick={() => setSearch("")}><FiX size={12} /></button>
          )}
        </div>
      </div>

      {/* Zone list */}
      {loading ? (
        <div className="sf-loading" style={{ height: "35vh" }}>
          <div className="spinner" style={{ width: 28, height: 28, borderColor: "var(--sp-emerald-500)", borderTopColor: "transparent" }} />
          <p style={{ marginTop: 12, color: "var(--sp-slate-400)", fontSize: 13 }}>Loading zones…</p>
        </div>
      ) : filteredZones.length === 0 ? (
        <div className="sf-empty">
          <FiMapPin size={40} color="#ddd" />
          <p>{search ? "No zones match your search" : "No zones found"}</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filteredZones.map((zone) => (
            <ZonePricingRow
              key={zone.zoneId || zone._id}
              zone={{ ...zone, prices: overviewMap[zone.zoneId]?.prices }}
              onSaved={loadAll}
              showToast={showToast}
            />
          ))}
        </div>
      )}

      <Toast toasts={toasts} />
    </div>
  );
}